import { useExportData } from "hooks/useExportData";
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";

import { ChemicalInventoryExportDialog } from "./ChemicalInventoryExportDialog";

type ExportTarget = {
  facilityId?: string;
  facilityName?: string;
  filter?: Record<string, unknown>;
};

type ChemicalInventoryExportContextValue = {
  tenantId?: string;
  isExporting: boolean;
  openExport: (target?: ExportTarget) => void;
};

const ChemicalInventoryExportContext = createContext<ChemicalInventoryExportContextValue | null>(
  null
);

export function ChemicalInventoryExportProvider({
  tenantId,
  children,
}: {
  tenantId?: string;
  children: ReactNode;
}) {
  const { startExport, status } = useExportData({ timeoutInSeconds: 900 });
  const [target, setTarget] = useState<ExportTarget | null>(null);
  const isExporting = status === "loading";

  const openExport = useCallback((nextTarget: ExportTarget = {}) => {
    setTarget(nextTarget);
  }, []);

  const value = useMemo(
    () => ({ tenantId, isExporting, openExport }),
    [tenantId, isExporting, openExport]
  );

  return (
    <ChemicalInventoryExportContext.Provider value={value}>
      {children}
      <ChemicalInventoryExportDialog
        open={target !== null}
        onClose={() => setTarget(null)}
        tenantId={tenantId}
        facilityId={target?.facilityId}
        facilityName={target?.facilityName}
        filter={target?.filter}
        startExport={startExport}
        status={status}
      />
    </ChemicalInventoryExportContext.Provider>
  );
}

export function useChemicalInventoryExport(): ChemicalInventoryExportContextValue {
  const context = useContext(ChemicalInventoryExportContext);
  if (!context) {
    throw new Error(
      "useChemicalInventoryExport must be used within ChemicalInventoryExportProvider"
    );
  }
  return context;
}
